// Region layout: validates the region case-insensitively (canonical lowercase in the URL), provides RegionContext, remembers the last region and shows the build-state banner (not ready, being updated) above the page.
import { createFileRoute, notFound, Outlet, redirect } from "@tanstack/react-router";
import { useEffect } from "react";

import { metaListJurisdictionsOptions } from "@/api/__generated__/@tanstack/react-query.gen";
import {
  findRegion,
  regionParam,
  selectableRegions,
  writeLastRegion,
  type RegionContextValue,
} from "@/app/region";
import { BuildingBanner } from "@/app/shell/BuildingBanner";
import { RegionNotFound } from "@/app/shell/RouteStates";

export const Route = createFileRoute("/_app/$region")({
  beforeLoad: async ({ context, params, location }): Promise<{ region: RegionContextValue }> => {
    const jurisdictions = await context.queryClient.query({
      ...metaListJurisdictionsOptions(),
      staleTime: "static",
    });
    const match = findRegion(selectableRegions(jurisdictions), params.region);
    if (match === undefined) {
      throw notFound({ routeId: "/_app/$region" });
    }
    const canonical = regionParam(match);
    if (params.region !== canonical) {
      const prefix = `/${params.region}`;
      const href = location.href.startsWith(prefix)
        ? `/${canonical}${location.href.slice(prefix.length)}`
        : `/${canonical}`;
      throw redirect({ href, replace: true });
    }
    return { region: { jurisdiction: match, currentDocUid: match.current_doc_uid } };
  },
  component: RegionLayout,
  notFoundComponent: RegionNotFound,
});

function RegionLayout() {
  const { region } = Route.useRouteContext();
  const param = regionParam(region.jurisdiction);
  useEffect(() => {
    writeLastRegion(param);
  }, [param]);
  return (
    <>
      <BuildingBanner currentDocUid={region.currentDocUid} />
      <Outlet />
    </>
  );
}
